import { useEffect, useRef, useState } from "react";
import type { Editor } from "@tiptap/core";
import { isInlineTextSpanColor } from "@/components/inlineTextSpan";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { toHexColor } from "./styleColor";
import { keepsTextSession, TEXT_SESSION_HOLD_PROPS } from "./textSessionFocus";

/**
 * The palette offered for a run's colour: a neutral ramp for de-emphasized
 * prose and a document-safe accent row, all dark enough to print legibly.
 */
const TEXT_COLOR_SWATCHES = [
  "#000000",
  "#434343",
  "#666666",
  "#999999",
  "#b7b7b7",
  "#cccccc",
  "#efefef",
  "#ffffff",
  "#c00000",
  "#e69138",
  "#bf9000",
  "#38761d",
  "#0b5394",
  "#351c75",
  "#741b47",
  "#a64d79",
];

interface TextColorPickerProps {
  /** The open session's TipTap instance, or null in block mode. */
  editor: Editor | null;
  /** The colour the target currently renders with. */
  value: string;
  /** Applies a colour to the target, or hands it back with null. */
  onApply: (color: string | null, keepFocus: boolean) => void;
  /** Paints a colour on the target live, without closing the popover. */
  onPreview: (color: string) => void;
  /** Commits the session, for when focus leaves it from the hex box. */
  onCommitSession: () => void;
}

/**
 * The colour popover's contents: the palette, the platform colour picker, a
 * box for a colour outside both, and the action handing the target's colour
 * back to what it inherits. Every swatch keeps focus where it is; the picker
 * and the hex box take it, and with a session open they hold the session
 * while they have it (the popover content carries the hold attribute). The
 * hex box applies nothing unless something was actually typed; the picker
 * paints the target live on every drag tick and settles when its dialog
 * commits.
 * @param editor - The open session's TipTap instance, or null in block mode
 * @param value - The colour the target currently renders with
 * @param onApply - Applies a colour, or hands it back with null
 * @param onPreview - Paints a colour live without closing the popover
 * @param onCommitSession - Commits the session
 * @returns The colour palette
 */
export function TextColorPicker({
  editor,
  value,
  onApply,
  onPreview,
  onCommitSession,
}: TextColorPickerProps) {
  const [hex, setHex] = useState(() => toHexColor(value));
  const [edited, setEdited] = useState(false);
  const [picked, setPicked] = useState(() => toHexColor(value));
  const pickerRef = useRef<HTMLInputElement>(null);
  const onApplyRef = useRef(onApply);
  onApplyRef.current = onApply;

  // The picker applies on the native `change` event - fired when the OS
  // dialog commits - not on every `input` tick of a drag. React's onChange is
  // the `input` event, so the commit listener is attached natively.
  useEffect(() => {
    const input = pickerRef.current;
    if (input === null) {
      return;
    }
    const commit = () => onApplyRef.current(input.value, true);
    input.addEventListener("change", commit);
    return () => input.removeEventListener("change", commit);
  }, []);

  return (
    <div className="flex w-52 flex-col gap-2">
      <div className="grid grid-cols-8 gap-1">
        {TEXT_COLOR_SWATCHES.map((color) => (
          <button
            key={color}
            type="button"
            title={color}
            aria-label={color}
            className={cn(
              "size-5 rounded border border-border transition-transform hover:scale-110",
              color.toLowerCase() === value.toLowerCase() &&
                "ring-1 ring-ring ring-offset-1",
            )}
            style={{ backgroundColor: color }}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => onApply(color, true)}
          />
        ))}
      </div>
      <div className="flex items-center gap-1">
        <div
          className="relative size-8 shrink-0 rounded-md border border-border"
          title="Pick a color"
        >
          <div
            className="pointer-events-none absolute inset-1 rounded"
            style={{ backgroundColor: picked }}
          />
          <input
            ref={pickerRef}
            type="color"
            className="absolute inset-0 size-full cursor-pointer opacity-0"
            aria-label="Pick a color"
            value={picked}
            onChange={(event) => {
              setPicked(event.target.value);
              onPreview(event.target.value);
            }}
            onBlur={(event) => {
              const staying =
                editor !== null &&
                keepsTextSession(editor, event.relatedTarget);
              if (editor !== null && !staying) {
                onCommitSession();
              }
            }}
          />
        </div>
        <Input
          {...TEXT_SESSION_HOLD_PROPS}
          className="h-8 flex-1 text-xs"
          aria-label="Custom color"
          placeholder="#000000"
          value={hex}
          onChange={(event) => {
            setHex(event.target.value);
            setEdited(true);
          }}
          onKeyDown={(event) => {
            if (event.key !== "Enter") {
              return;
            }
            event.preventDefault();
            if (edited) {
              applyHex(event.currentTarget.value, true, onApply);
            }
          }}
          onBlur={(event) => {
            const staying =
              editor !== null && keepsTextSession(editor, event.relatedTarget);
            if (edited) {
              applyHex(event.currentTarget.value, staying, onApply);
            }
            if (editor !== null && !staying) {
              onCommitSession();
            }
          }}
        />
        <Button
          variant="ghost"
          size="sm"
          className="h-8 text-xs"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => onApply(null, true)}
        >
          Default
        </Button>
      </div>
    </div>
  );
}

/**
 * Applies a typed colour, ignoring anything that is not a colour we store.
 * @param typed - The raw hex box contents
 * @param keepFocus - Whether to hand focus back to the session
 * @param onApply - Applies a colour to the target
 */
function applyHex(
  typed: string,
  keepFocus: boolean,
  onApply: TextColorPickerProps["onApply"],
): void {
  const trimmed = typed.trim();
  if (isInlineTextSpanColor(trimmed)) {
    onApply(trimmed, keepFocus);
  }
}
